'use client';

import clsx from 'clsx';
import { FC } from 'react';

import { CarComparisonAdd } from 'src/car-comparison/components/CarComparisonAdd/CarComparisonAdd';
import { PROMO_4 } from 'src/constants/constants';
import { IconWatchingPeople } from 'src/general/Icons/IconWatchingPeople';
import { Link } from 'src/general/components/Link/Link';
import { ProductImage } from 'src/general/components/ProductImage/ProductImage';
import { PromoPill } from 'src/general/components/PromoPill/PromoPill';
import { Shade } from 'src/general/components/Shade/Shade';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { BaseCarData } from 'src/types/Car.types';

import { AddToWatchListButton } from '../AddToWatchList/AddToWatchListButton';
import { AddToWatchListIcon } from '../AddToWatchList/AddToWatchListIcon';
import { StatusPill } from '../StatusPill/StatusPill';
import { ProductCardRepaymentsTooltip } from './ProductCardRepaymentsTooltip';

type props = {
  carData: BaseCarData;
  weeklyRepayment: number;
  defaultRate: number;
  imagePriority?: boolean;
  onCardClick?: () => void;
  onWatchlistStatusChange?: () => Promise<void>;
  imageTestId?: string;
} & (
  | {
      variant: 'plp';
    }
  | {
      variant: 'default';
    }
);

export const ProductCard: FC<props> = ({
  carData,
  weeklyRepayment,
  defaultRate,
  imagePriority,
  onWatchlistStatusChange,
  onCardClick,
  imageTestId,
  ...props
}) => {
  const priceString = formatNumber(carData.price, { prepend: '$' });
  const dutyString = formatNumber(carData.dutyPrice, { prepend: '$' });
  const weeklyRepaymentString = formatNumber(weeklyRepayment, { prepend: '$' });
  const defaultRateString = formatNumber(defaultRate * 100, { decimals: 2, trimZeroDecimals: true, append: '%' });

  const promoPillData = () => {
    const result = [];
    if (carData.promotionTags?.includes(PROMO_4)) {
      result.push({
        key: PROMO_4,
        copy: 'Bonus Extended Coverage',
        backgroundColor: '#ebf2f5',
        color: 'rgba(21, 13, 44, 0.878431)',
      });
    }
    return result;
  };

  return (
    <div
      className={clsx('ProductCard', `ProductCard-${props.variant}`)}
      data-testid={`ProductCard-container-${carData.sku}`}
    >
      <Link
        href={`/product-detail/${carData.sku}`}
        onClick={onCardClick}
        className={clsx('ProductCard-link', 'Card-link', 'tileLink', 'c-shade_container', 'c-shade_container--grey')}
        data-testid="used-car-product-card"
      >
        <Shade />
        <span className="sr-only">{carData.model}</span>
      </Link>
      <ProductImage
        key={carData.sku}
        src={carData.imageUrl}
        alt={carData.model}
        className="ProductCard-image"
        imagePriority={imagePriority}
        data-testid={imageTestId}
      />
      <p
        className={clsx('ProductCard-model', 'c-fs-body1', 'font-extrabold')}
        data-testid="ProductCard-component-titleText"
      >
        {carData.model}
      </p>
      <p className={clsx('ProductCard-variant', 'c-fs-body2')} data-testid="ProductCard-component-variantText">
        {carData.variant}
      </p>
      <p className={clsx('ProductCard-details', 'c-fs-body2')}>
        <span data-testid="ProductCard-component-distanceText">
          {formatNumber(carData.kilometers, { append: ' km' })}
        </span>
        <span>&ensp;•&ensp;</span>
        <span data-testid="ProductCard-component-transmissionText">{carData.transmission}</span>
      </p>
      <div className="ProductCard-statusPillWrapper">
        <StatusPill sku={carData.sku} state={carData.state} />
      </div>
      {carData.state === 'available' && <PromoPill promoPillData={promoPillData()} />}
      {props.variant === 'default' && (
        <aside className="ProductCard-controls">
          <AddToWatchListIcon
            key={carData.sku}
            className="ProductCard-watchlist"
            sku={carData.sku}
            product={{
              ...carData,
              title: carData.model,
              price: priceString,
              dutyPrice: dutyString,
            }}
            onWatchlistStatusChange={onWatchlistStatusChange}
          />
        </aside>
      )}
      <footer className="ProductCard-footer">
        {carData.watchers ? (
          <div className="ProductCard-watchers">
            <IconWatchingPeople />
            <span className="c-fs-caption2">
              <b>{carData.watchers} others</b> watching this car
            </span>
          </div>
        ) : null}
        <p
          className={clsx('ProductCard-price', 'c-fs-h5', 'font-extrabold')}
          data-testid="ProductCard-component-priceText"
        >
          {priceString}
        </p>
        <div
          className={clsx('ProductCard-repayment', 'c-fs-caption2')}
          data-testid="ProductCard-component-repaymentText"
        >
          <span>
            est. <strong>{weeklyRepaymentString}/wk</strong> based on <strong>{defaultRateString}</strong> p.a.
          </span>{' '}
          <ProductCardRepaymentsTooltip weeklyRepayment={weeklyRepaymentString} defaultRate={defaultRateString} />
        </div>

        {props.variant !== 'default' && (
          <aside className="ProductCard-footerActions">
            <CarComparisonAdd sku={carData.sku} className="ProductCard-footerActionButton" />
            <AddToWatchListButton
              key={carData.sku}
              className="ProductCard-footerActionButton"
              sku={carData.sku}
              product={{
                ...carData,
                title: carData.model,
                price: priceString,
                dutyPrice: dutyString,
              }}
              onWatchlistStatusChange={onWatchlistStatusChange}
              buttonProps={{
                variant: 'secondary',
                fullWidth: true,
              }}
            />
          </aside>
        )}
      </footer>
    </div>
  );
};
